// Author: Tejal Komb

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  Badge,
  useToast,
  Button,
  IconButton,
  Tooltip,
  HStack,
} from "@chakra-ui/react";
import { DownloadIcon, EmailIcon, BellIcon } from "@chakra-ui/icons";
import { jsPDF } from "jspdf";

const TransactionTable = () => {
  const [transactions, setTransactions] = useState([]);
  const toast = useToast();

  const generateReceipt = (txn) => {
    const doc = new jsPDF();

    // Design Elements
    doc.setFillColor(22, 58, 122); // Brand Blue
    doc.rect(0, 0, 210, 40, "F");

    // Header
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("GYAN", 105, 20, { align: "center" });
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text("Payment Receipt", 105, 30, { align: "center" });

    // Body Setup
    doc.setTextColor(40, 40, 40);
    doc.setFontSize(12);

    // Receipt Information Table Header
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 50, 190, 50);

    let yPos = 65;
    const addLine = (label, value) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, 20, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(`${value}`, 60, yPos);
      yPos += 12;
    };

    addLine("Receipt No", `RC-${txn.id}-${Date.now().toString().slice(-4)}`);
    addLine("Date", new Date().toLocaleDateString());
    addLine("Recipient", `${txn.fname} ${txn.lname}`);
    addLine("Email", txn.user);

    yPos += 5;
    doc.line(20, yPos, 190, yPos);
    yPos += 15;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Transaction Details", 20, yPos);
    yPos += 15;

    doc.setFontSize(12);
    addLine("Internship", txn.internship);
    addLine("Payment ID", txn.payment_id);
    addLine("Amount Paid", `INR ${txn.payment_amount}`);
    addLine("Status", txn.payment_status);

    yPos += 10;
    doc.line(20, yPos, 190, yPos);
    yPos += 20;

    // Footer
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("This is a computer-generated receipt and requires no signature.", 105, yPos, { align: "center" });
    doc.text("Thank you for choosing Gyan!", 105, yPos + 7, { align: "center" });
    doc.text("aim4u.co.in", 105, yPos + 14, { align: "center" });

    doc.save(`Receipt_${txn.fname}_${txn.payment_id}.pdf`);
  };

  const sendReceiptEmail = async (txn) => {
    const doc = new jsPDF();

    // Design Elements
    doc.setFillColor(22, 58, 122); // Brand Blue
    doc.rect(0, 0, 210, 40, "F");

    // Header
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("GYAN", 105, 20, { align: "center" });
    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text("Payment Receipt", 105, 30, { align: "center" });

    // Body Setup
    doc.setTextColor(40, 40, 40);
    doc.setFontSize(12);

    // Receipt Information Table Header
    doc.setDrawColor(200, 200, 200);
    doc.line(20, 50, 190, 50);

    let yPos = 65;
    const addLine = (label, value) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${label}:`, 20, yPos);
      doc.setFont("helvetica", "normal");
      doc.text(`${value}`, 60, yPos);
      yPos += 12;
    };

    addLine("Receipt No", `RC-${txn.id}-${Date.now().toString().slice(-4)}`);
    addLine("Date", new Date().toLocaleDateString());
    addLine("Recipient", `${txn.fname} ${txn.lname}`);
    addLine("Email", txn.user);

    yPos += 5;
    doc.line(20, yPos, 190, yPos);
    yPos += 15;

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Transaction Details", 20, yPos);
    yPos += 15;

    doc.setFontSize(12);
    addLine("Internship", txn.internship);
    addLine("Payment ID", txn.payment_id);
    addLine("Amount Paid", `INR ${txn.payment_amount}`);
    addLine("Status", txn.payment_status);

    yPos += 10;
    doc.line(20, yPos, 190, yPos);
    yPos += 20;

    // Footer
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text("This is a computer-generated receipt and requires no signature.", 105, yPos, { align: "center" });
    doc.text("Thank you for choosing Gyan!", 105, yPos + 7, { align: "center" });
    doc.text("aim4u.co.in", 105, yPos + 14, { align: "center" });

    const pdfBase64 = doc.output("datauristring");
    const accessToken = sessionStorage.getItem("accessToken");

    try {
      const backendHost = "http://localhost:8000"; // USE LOCAL FOR EMAIL SENDING
      await axios.post(
        `${backendHost}/super/send-receipt/`,
        {
          email: txn.user,
          pdf: pdfBase64,
          filename: `Receipt_${txn.fname}_${txn.payment_id}.pdf`,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      toast({
        title: "Email Sent",
        description: `Receipt has been sent to ${txn.user}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Email Failed",
        description: error.response?.data?.error || "Could not send the receipt email.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const sendReminderEmail = async (txn) => {
    const accessToken = sessionStorage.getItem("accessToken");
    try {
      const backendHost = "http://localhost:8000";
      await axios.post(
        `${backendHost}/super/send-reminder/`,
        {
          email: txn.user,
          internship: txn.internship
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      toast({
        title: "Reminder Sent",
        description: `Reminder has been sent to ${txn.user}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: "Failed to Send Reminder",
        description: error.response?.data?.error || "Could not send the reminder email.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getTransactions = async () => {
    try {
      const accessToken = sessionStorage.getItem("accessToken");
      const backendHost = "https://gyanbackend.aim4u.co.in"; // USE PROD FOR FETCHING DATA

      const response = await axios.get(
        `${backendHost}/super/view-applied-interns/`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      const allData = response.data.applications;

      // Only keep rows with a valid payment status
      const filtered = allData.filter(
        (entry) => entry.payment_status !== null && entry.payment_id
      );

      setTransactions(filtered);
    } catch (error) {
      console.error("Failed to load transactions:", error);
      toast({
        title: "Failed to fetch transactions",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getTransactions();
  }, []);

  return (
    <Box bg="#e8f3fd" w="full" minH="100vh" p="20px">
      <Text fontSize="35px" fontWeight="bold" ml="80px" mb="20px" color="green.500">
        Payment Transactions
      </Text>

      <TableContainer bg="white" borderRadius="10px" boxShadow="lg" mx="80px">
        <Table variant="simple" colorScheme="blue">
          <Thead bg="blue.600">
            <Tr>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Sr No.</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Name</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Email</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Internship</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Amount</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Payment ID</Th>
              <Th color="white" borderRight="1px solid" borderColor="blue.100" textAlign="center">Status</Th>
              <Th color="white" textAlign="center">Receipts</Th>
            </Tr>
          </Thead>
          <Tbody>
            {transactions.map((txn, index) => (
              <Tr key={txn.id} _hover={{ bg: "blue.50" }}>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center" color="gray.700">{index + 1}</Td>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center" color="gray.800" fontWeight="medium"> {txn.fname} {txn.lname}</Td>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center" color="gray.700">{txn.user}</Td>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center" color="gray.700">{txn.internship}</Td>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center" color="gray.700">₹{txn.payment_amount}</Td>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center" color="gray.700">{txn.payment_id}</Td>
                <Td borderRight="1px solid" borderColor="blue.100" textAlign="center">
                  <Badge colorScheme={txn.payment_status === "COMPLETED" ? "green" : "red"} >
                    {txn.payment_status}
                  </Badge>
                </Td>
                <Td textAlign="center">
                  <HStack justify="center" spacing={1}>
                    {txn.payment_status === "COMPLETED" && txn.status === "Approved" ? (
                      <>
                        <Tooltip label="Download Receipt">
                          <IconButton
                            icon={<DownloadIcon />}
                            colorScheme="green"
                            variant="ghost"
                            size="sm"
                            onClick={() => generateReceipt(txn)}
                            aria-label="Download Receipt"
                            _hover={{ bg: "green.100", transform: "scale(1.1)" }}
                          />
                        </Tooltip>
                        <Tooltip label="Send Receipt to Email">
                          <IconButton
                            icon={<EmailIcon />}
                            colorScheme="blue"
                            variant="ghost"
                            size="sm"
                            onClick={() => sendReceiptEmail(txn)}
                            aria-label="Send Receipt Email"
                            _hover={{ bg: "blue.100", transform: "scale(1.1)" }}
                          />
                        </Tooltip>
                      </>
                    ) : (
                      <Text fontSize="xs" color="gray.400">N/A</Text>
                    )}

                    {txn.payment_status !== "COMPLETED" && (
                      <Tooltip label="Send Payment Reminder">
                        <IconButton
                          icon={<BellIcon />}
                          colorScheme="yellow"
                          variant="ghost"
                          size="sm"
                          onClick={() => sendReminderEmail(txn)}
                          aria-label="Send Reminder"
                          _hover={{ bg: "yellow.100", transform: "scale(1.1)" }}
                        />
                      </Tooltip>
                    )}
                  </HStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TransactionTable;
